// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, of } from 'rxjs';
import { mergeMap, takeUntil, tap } from 'rxjs/operators';
import { NetworkService } from '@core/services/network.service';
import { LoggerService } from '@core/services/logger.service';
import { FeatureFlagsStore } from '@core/store/feature-flags.store';
import { DataStore } from '@core/store/data.store';
import { Auth2Service } from '@core/auth/auth.service';
import { OfflineStore } from './offline-store.service';
import { OfflineFreshnessService } from './offline-freshness.service';
import { OfflineWindowSyncService } from './offline-window-sync.service';

// Read endpoints the offline cache captures and can serve (plan B2 — the
// registry the spike proved; the generated API clients cannot carry an
// HttpContext opt-in, so URL selectivity lives at the seam). Keys are the
// ORIGINAL relative URLs with params: this interceptor runs BEFORE
// httpHandlerInterceptor's environment rewrite, so keys are environment-
// independent and never carry auth material.
const FLIGHT_LIST_RE = /^\/api\/pwi\/flights(\?.*)?$/; // the flight list — never /flights/<sub-route>
const CACHEABLE_READS: RegExp[] = [
  FLIGHT_LIST_RE,
  /^\/api\/pwi\/flights\/[^/]+\/detail$/, // aggregated flight-detail bundle
  /^\/api\/pwi\/crew-match-errors\/types$/, // static list the detail view loads alongside
];

/**
 * The offline read path (EF-2.1/2.2, EF-4.1): while online, cacheable reads
 * are teed into the encrypted per-pilot store; while the app is unreachable,
 * they are served back as synthesized responses the stores cannot tell from
 * the network's. Mechanism proven by the spike on web + iOS (interceptor
 * composes with CapacitorHttp's native tunnel); the storage underneath is
 * B1's SQLCipher vault.
 *
 * Dark behind the OfflineMode flag, and NEVER active in observation mode:
 * impersonated content must not persist beyond the session (EF-5.5 / EF-9.2),
 * and the real pilot's cache must not serve under an impersonated session
 * either. The vault key is `<company>:<crewCode>` (EF-9.1's two dimensions);
 * until the user is loaded there is no key, so nothing is cached.
 *
 * MUST be listed BEFORE httpHandlerInterceptor: it keys on the clean relative
 * URL, and an offline cache hit must answer before the handler's
 * device-offline fail-fast. A cache miss falls through — device-offline
 * fail-fasts as always, and server-unreachable requests go out as A1's
 * recovery probes. (Cached serves don't probe; the uncached traffic —
 * tracking POSTs included — still does, so the evidence can still clear.)
 */
export const offlineCacheInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET' || !CACHEABLE_READS.some((re) => re.test(req.urlWithParams))) {
    return next(req);
  }

  const flags = inject(FeatureFlagsStore);
  const ds = inject(DataStore);
  const network = inject(NetworkService);
  const store = inject(OfflineStore);
  const freshness = inject(OfflineFreshnessService);
  const windowSync = inject(OfflineWindowSyncService);
  const logger = inject(LoggerService);

  const auth = inject(Auth2Service);
  if (auth.sessionInvalidated()) return next(req);

  const domain = ds.claims()?.ergossPwiDomain ?? '';
  const crew = ds.userCode();
  if (!flags.isEnabled('OfflineMode') || ds.isImpersonating() || !domain || !crew) {
    return next(req);
  }

  const pilotKey = `${domain}:${crew}`;
  const cacheKey = req.urlWithParams;

  if (!network.isOnline()) {
    return from(store.getRead(pilotKey, cacheKey)).pipe(
      mergeMap((hit) => {
        if (auth.sessionInvalidated() || !hit) return next(req);
        // info, not debug: offline serves are rare, high-signal events; the
        // capture timestamp is the freshness stamp the chip surfaces (B3).
        logger.info(
          'OfflineCache',
          `serving from the vault (captured ${new Date(hit.capturedAt).toISOString()})`,
          cacheKey,
        );
        freshness.reportVaultServe(cacheKey, hit.capturedAt);
        return of(new HttpResponse({ status: 200, body: hit.body, url: req.url }));
      }),
      takeUntil(auth.sessionEnded$),
    );
  }

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.ok) {
        // Fire and forget — the response is never held hostage to the write.
        void store.setRead(pilotKey, cacheKey, event.body);
        // A fresh flight LIST defines the window: hand it to the window sync
        // (prefetch missing details + retention reconciliation, B2.5/B4).
        if (FLIGHT_LIST_RE.test(cacheKey)) {
          windowSync.onListCaptured(pilotKey, event.body);
        }
      }
    }),
  );
};
